import { canonicalPlayerKey } from './aggregate';

// A player's competitive record, computed from the leaderboard rather than from replays.
//
// This is the one part of the profile that is not about *how* someone solves: it is what they
// have done. It comes from the `leaderboard` table directly, so it covers every completion —
// including the ones attribution skips (a puzzle with no unique solution has no decidable
// mistakes, but it was still solved).

/** One leaderboard row, with the assists it was recorded under. */
export interface RecordEntry {
  puzzleId: number;
  playerName: string;
  timeMs: number;
  /** 1-based, per (puzzle, player token) in submission order. */
  attemptNumber: number;
  usedAutoXs: boolean;
  usedHints: boolean;
  /** Played on a new player's board, with some queens revealed from the start. */
  usedReveal: boolean;
  /** Submission id — the tie-break, so equal times resolve to whoever got there first. */
  id: number;
}

export interface PlayerRecord {
  solves: number;
  wins: number;
  timePlayedMs: number;
}

export const emptyRecord = (): PlayerRecord => ({ solves: 0, wins: 0, timePlayedMs: 0 });

/**
 * How assisted a completion was, low to high: clean, Auto Xs, a repeat attempt, hints, revealed
 * queens. An entry carrying several tags takes the highest — you do not get the clean-run credit
 * for a run that also used hints.
 *
 * Revealed queens sit at the bottom because they are the only assist that hands over part of the
 * answer: hints tell a beginner what to look at, a new player's board tells them where three of
 * the queens go. It also means the new-player window costs nobody their win — a beginner's first
 * three boards cannot take one off the player who solved them unaided.
 */
export function assistClass(e: RecordEntry): number {
  if (e.usedReveal) return 4;
  if (e.usedHints) return 3;
  if (e.attemptNumber > 1) return 2;
  if (e.usedAutoXs) return 1;
  return 0;
}

/**
 * Did this entry win its puzzle?
 *
 * A win means nobody got there first under the same terms or better ones. Concretely, an entry is
 * beaten by any entry in a *less* assisted class (however slow — a clean solve outranks an
 * assisted one regardless of the clock) and by a faster entry in its *own* class. So the winner
 * of a puzzle is the fastest entry in the least-assisted class anyone played it in: a first-clean
 * run beats an Auto-Xs run that was quicker, and an Auto-Xs run only wins a board where nobody
 * played it clean.
 *
 * Entries by the same person under different tokens compete like anyone else's, which is what
 * keeps one person from taking two wins on one puzzle.
 */
export function isWin(entry: RecordEntry, samePuzzle: RecordEntry[]): boolean {
  const c = assistClass(entry);
  return !samePuzzle.some(o => {
    if (o.id === entry.id) return false;
    const oc = assistClass(o);
    if (oc < c) return true;
    return oc === c && (o.timeMs < entry.timeMs || (o.timeMs === entry.timeMs && o.id < entry.id));
  });
}

/** Solves, wins and total time on the clock, per merged identity. */
export function computeRecords(entries: RecordEntry[]): Map<string, PlayerRecord> {
  const byPuzzle = new Map<number, RecordEntry[]>();
  for (const e of entries) {
    (byPuzzle.get(e.puzzleId) ?? byPuzzle.set(e.puzzleId, []).get(e.puzzleId)!).push(e);
  }

  const out = new Map<string, PlayerRecord>();
  for (const e of entries) {
    const key = canonicalPlayerKey(e.playerName);
    const rec = out.get(key) ?? out.set(key, emptyRecord()).get(key)!;
    rec.solves++;
    rec.timePlayedMs += e.timeMs;
    if (isWin(e, byPuzzle.get(e.puzzleId)!)) rec.wins++;
  }
  return out;
}
